import { RowActions } from "@/components/common/RowActions";
import { unitLabel } from "@/components/common/UnitPicker";
import type { Product } from "@/features/products/types";
import { formatMoney, formatQuantity } from "@/lib/format";

type ProductListProps = {
  products: Product[];
  currency: string;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
};

export function ProductList({ products, currency, onEdit, onDelete }: ProductListProps) {
  return (
    <>
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <table className="w-full text-left">
          <thead className="bg-muted/60">
            <tr className="text-[13px] font-semibold text-muted-foreground">
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3 w-28">Unité</th>
              <th className="px-4 py-3 w-40">Se vend par</th>
              <th className="px-4 py-3 w-36 text-right">Prix de vente</th>
              <th className="px-4 py-3 w-24 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className="border-t border-muted">
                <td className="px-4 py-3.5">
                  <p className="font-medium">{product.name}</p>
                  {product.description ? (
                    <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{product.description}</p>
                  ) : null}
                </td>
                <td className="px-4 py-3.5 text-[15px]">{unitLabel(product.measurementUnit)}</td>
                <td className="px-4 py-3.5 text-[15px] tabular-nums">
                  {formatQuantity(product.measurementPrecision, unitLabel(product.measurementUnit))}
                </td>
                <td className="px-4 py-3.5 text-right text-[15px] font-semibold tabular-nums">
                  {formatMoney(product.price, currency)}
                </td>
                <td className="px-2 py-3.5">
                  <div className="flex justify-end">
                    <RowActions onEdit={() => onEdit(product)} onDelete={() => onDelete(product)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="flex flex-col gap-3 md:hidden">
        {products.map((product) => (
          <li key={product.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 flex-1 font-semibold">{product.name}</p>
              <p className="shrink-0 font-semibold tabular-nums">{formatMoney(product.price, currency)}</p>
            </div>
            {product.description ? (
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{product.description}</p>
            ) : null}
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Se vend par{" "}
                <span className="font-medium text-foreground tabular-nums">
                  {formatQuantity(product.measurementPrecision, unitLabel(product.measurementUnit))}
                </span>
              </p>
              <RowActions onEdit={() => onEdit(product)} onDelete={() => onDelete(product)} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
